import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getDoctorAppointments,
  getAllPendingAppointments,
  getDoctorStats,
} from "../../Redux/actions/doctorActions/doctorActions.js";
import Loading from "../../components/Loading.jsx";
import Error from "../../components/Error.jsx";
import ConsultPatientModal from "../../components/doctorComponents/ConsultPatientModal.jsx";
import ViewPatientHistoryModal from "../../components/doctorComponents/ViewPatientHistoryModal.jsx";
import CompletedAppointmentsHistory from "../../components/doctorComponents/CompletedAppointmentsHistory.jsx";
import "./DoctorDashboard.css";
import { useSanitizedInput } from "../../hooks/useSanitizedInput";

// ✅ NEW: Utility function to convert 24-hour time to 12-hour format
const convertTo12Hour = (time24) => {
  if (!time24) return "N/A";
  
  try {
    const [hours, minutes] = time24.split(':');
    let hour = parseInt(hours);
    const minute = minutes || '00';
    const ampm = hour >= 12 ? 'PM' : 'AM';
    
    hour = hour % 12;
    hour = hour ? hour : 12; // 0 should be 12
    
    return `${hour}:${minute} ${ampm}`;
  } catch (error) {
    return time24; // Return original if conversion fails
  }
};

const DoctorDashboard = () => {
  const dispatch = useDispatch();
  const { userInfo } = useSelector((state) => state.auth);
  const {
    appointments = [],
    appointmentsLoading,
    appointmentsError,
    allPendingAppointments = [],
    allPendingLoading,
    stats,
    statsLoading,
  } = useSelector((state) => state.doctor);

  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [showConsultModal, setShowConsultModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showCompletedHistory, setShowCompletedHistory] = useState(false);
  const [historyPatientId, setHistoryPatientId] = useState(null);

  const [searchMRCode, handleSearchChange, resetSearch] = useSanitizedInput("", "mrcode");
  const [activeTab, setActiveTab] = useState("today");
  const [filterStatus, setFilterStatus] = useState("all");

  useEffect(() => {
    dispatch(getDoctorAppointments());
    dispatch(getAllPendingAppointments());
    dispatch(getDoctorStats());

    const interval = setInterval(() => {
      dispatch(getDoctorAppointments());
      dispatch(getAllPendingAppointments());
      dispatch(getDoctorStats());
    }, 30000);

    return () => clearInterval(interval);
  }, [dispatch]);

  const handleSearch = () => {
    if (searchMRCode.trim()) {
      dispatch(getDoctorAppointments(searchMRCode));
    }
  };

  const handleClearSearch = () => {
    resetSearch();
    setFilterStatus("all");
    setActiveTab("today");
    dispatch(getDoctorAppointments());
  };

  const handleConsultClick = (appointment) => {
    setSelectedAppointment(appointment);
    setShowConsultModal(true);
  };

  const handleViewHistory = (patientId) => {
    setHistoryPatientId(patientId);
    setShowHistoryModal(true);
  };

  const getPriorityBadge = (priority) => {
    const badges = {
      "Most Urgent": "danger",
      Urgent: "warning",
      Normal: "info",
    };
    return badges[priority] || "info";
  };

  const getStatusBadge = (status) => {
    const badges = {
      confirmed: { bg: "success", text: "Confirmed" },
      "on-hold": { bg: "warning", text: "On Hold" },
      completed: { bg: "secondary", text: "Completed" },
    };
    return badges[status] || { bg: "primary", text: status };
  };

  // ✅ NEW: Get waiting time badge color
  const getWaitingTimeBadge = (waitingTime) => {
    if (!waitingTime) return "secondary";
    if (waitingTime <= 15) return "success";
    if (waitingTime <= 30) return "warning";
    return "danger";
  };

  const getCurrentAppointments = () => {
    let appts = activeTab === "today" ? appointments : allPendingAppointments;

    if (filterStatus !== "all") {
      appts = appts.filter((appt) => appt.status === filterStatus);
    }

    return appts;
  };

  const currentAppointments = getCurrentAppointments();

  return (
    <div className="doctor-dashboard">
      <div className="container-fluid py-4">
        {/* Header with Quick Stats */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="card border-0 shadow-lg bg-gradient-primary text-white">
              <div className="card-body p-4">
                <div className="d-flex justify-content-between align-items-center flex-wrap">
                  <div>
                    <h3 className="mb-1 fw-bold">
                      <i className="bi bi-person-circle me-2"></i>
                      Welcome, Dr.{" "}
                      {userInfo?.user?.name || userInfo?.name || "Doctor"}
                    </h3>
                    <p className="mb-0 opacity-75">
                      <i className="bi bi-calendar-check me-2"></i>
                      {new Date().toLocaleDateString("en-US", {
                        weekday: "long",
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </p>
                  </div>
                  <button
                    className="btn btn-light btn-lg"
                    onClick={() => setShowCompletedHistory(true)}
                  >
                    <i className="bi bi-clock-history me-2"></i>
                    Appointment History
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Statistics Cards */}
        {/* {statsLoading ? (
          <div className="row mb-4">
            <div className="col-12">
              <Loading />
            </div>
          </div>
        ) : (
          stats && (
            <div className="row g-4 mb-4">
              <div className="col-md-3 col-sm-6">
                <div className="card border-0 shadow-sm h-100 stat-card bg-gradient-info text-white">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <p className="mb-1 opacity-75">Today's Appointments</p>
                        <h2 className="mb-0 fw-bold">
                          {stats.todayAppointments || 0}
                        </h2>
                      </div>
                      <div className="stat-icon">
                        <i className="bi bi-calendar-event"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-md-3 col-sm-6">
                <div className="card border-0 shadow-sm h-100 stat-card bg-gradient-success text-white">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <p className="mb-1 opacity-75">Completed Today</p>
                        <h2 className="mb-0 fw-bold">
                          {stats.completedToday || 0}
                        </h2>
                      </div>
                      <div className="stat-icon">
                        <i className="bi bi-check-circle"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-md-3 col-sm-6">
                <div className="card border-0 shadow-sm h-100 stat-card bg-gradient-warning text-white">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <p className="mb-1 opacity-75">On Hold</p>
                        <h2 className="mb-0 fw-bold">
                          {stats.onHoldToday || 0}
                        </h2>
                      </div>
                      <div className="stat-icon">
                        <i className="bi bi-hourglass-split"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-md-3 col-sm-6">
                <div className="card border-0 shadow-sm h-100 stat-card bg-gradient-danger text-white">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <p className="mb-1 opacity-75">Total Patients</p>
                        <h2 className="mb-0 fw-bold">
                          {stats.totalPatients || 0}
                        </h2>
                      </div>
                      <div className="stat-icon">
                        <i className="bi bi-people"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )
        )} */}

        {/* Search & Filter Section */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-body">
                <div className="row g-3 align-items-end">
                  <div className="col-md-5">
                    <label className="form-label fw-semibold">
                      <i className="bi bi-search me-2"></i>
                      Search by MR-Code
                    </label>

                    <div className="input-group">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="e.g., MR-00001"
                        aria-label="Search MR Code"
                        aria-describedby="button-search"
                        value={searchMRCode}
                        onChange={handleSearchChange}
                        onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                        maxLength={50}
                      />

                      <button
                        className="btn btn-outline-primary"
                        type="button"
                        id="button-search"
                        onClick={handleSearch}
                        disabled={!searchMRCode.trim()}
                      >
                        <i className="bi bi-search me-1"></i>
                        Search
                      </button>
                    </div>
                  </div>

                  <div className="col-md-3">
                    <label className="form-label fw-semibold">
                      <i className="bi bi-funnel me-2"></i>
                      Filter Status
                    </label>

                    <select
                      className="form-select"
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                    >
                      <option value="all">All Status</option>
                      <option value="confirmed">Confirmed</option>
                      <option value="on-hold">On Hold</option>
                    </select>
                  </div>

                  <div className="col-md-2">
                    <button
                      className="btn btn-outline-primary w-100"
                      onClick={handleClearSearch}
                    >
                      <i className="bi bi-x-circle me-1"></i>
                      Clear
                    </button>
                  </div>

                  <div className="col-md-2">
                    <button
                      className="btn btn-outline-primary w-100"
                      onClick={() => {
                        dispatch(getDoctorAppointments());
                        dispatch(getAllPendingAppointments());
                        dispatch(getDoctorStats());
                      }}
                    >
                      <i className="bi bi-arrow-clockwise me-1"></i>
                      Refresh
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Appointments Section with Tabs */}
        <div className="row">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white border-bottom-0 pt-4">
                <ul className="nav nav-pills mb-3" role="tablist">
                  <li className="nav-item" role="presentation">
                    <button
                      className={`nav-link ${
                        activeTab === "today" ? "active" : ""
                      }`}
                      onClick={() => setActiveTab("today")}
                    >
                      <i className="bi bi-calendar-day me-2"></i>
                      Today's Appointments
                      <span className="badge bg-primary ms-2">
                        {appointments.length}
                      </span>
                    </button>
                  </li>
                  <li className="nav-item" role="presentation">
                    <button
                      className={`nav-link ${
                        activeTab === "all-pending" ? "active" : ""
                      }`}
                      onClick={() => setActiveTab("all-pending")}
                    >
                      <i className="bi bi-list-task me-2"></i>
                      All Pending (Including Backlog)
                      <span className="badge bg-warning ms-2">
                        {allPendingAppointments.length}
                      </span>
                    </button>
                  </li>
                </ul>
              </div>

              <div className="card-body p-0">
                {(appointmentsLoading || allPendingLoading) && (
                  <div className="p-4">
                    <Loading />
                  </div>
                )}

                {appointmentsError && (
                  <div className="p-4">
                    <Error message={appointmentsError} />
                  </div>
                )}

                {!appointmentsLoading &&
                  !allPendingLoading &&
                  !appointmentsError && (
                    <>
                      {currentAppointments.length === 0 ? (
                        <div className="text-center py-5">
                          <i className="bi bi-calendar-x display-1 text-muted"></i>
                          <p className="mt-3 text-muted mb-0">
                            {searchMRCode
                              ? "No appointments found for this MR-Code"
                              : filterStatus !== "all"
                              ? `No ${filterStatus} appointments`
                              : activeTab === "today"
                              ? "No appointments scheduled for today"
                              : "No pending appointments"}
                          </p>
                        </div>
                      ) : (
                        <div className="table-responsive">
                          <table className="table table-hover mb-0">
                            <thead className="table-light">
                              <tr>
                                <th className="text-center" style={{ width: "60px" }}>#</th>
                                <th style={{ width: "100px" }}>Date</th>
                                <th style={{ width: "100px" }}>Status</th>
                                <th style={{ width: "100px" }}>Priority</th>
                                <th>Patient Info</th>
                                <th style={{ width: "120px" }}>MR Code</th>
                                <th style={{ width: "150px" }}>Vitals</th>
                                {/* ✅ CHANGED: Updated Time column header and added Waiting Time */}
                                <th style={{ width: "100px" }}>Time</th>
                                <th style={{ width: "100px" }}>Wait Time</th>
                                <th style={{ width: "130px" }}>Department</th>
                                <th style={{ width: "220px" }} className="text-center">Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {currentAppointments.map((appt) => (
                                <tr key={appt._id} className="appointment-row align-middle">
                                  <td className="text-center fw-bold">
                                    {appt.appointmentNumber}
                                  </td>
                                  <td>
                                    <small>{appt.date}</small>
                                  </td>
                                  <td>
                                    <span
                                      className={`badge bg-${
                                        getStatusBadge(appt.status).bg
                                      } d-inline-flex align-items-center`}
                                    >
                                      {getStatusBadge(appt.status).text}
                                      {appt.status === "on-hold" &&
                                        appt.onHoldReason && (
                                          <i
                                            className="bi bi-info-circle ms-1"
                                            title={appt.onHoldReason}
                                            style={{ cursor: "pointer" }}
                                          ></i>
                                        )}
                                    </span>
                                  </td>
                                  <td>
                                    <span
                                      className={`badge bg-${getPriorityBadge(
                                        appt.priority
                                      )}`}
                                    >
                                      {appt.priority}
                                    </span>
                                  </td>
                                  <td>
                                    <div>
                                      <strong className="d-block">
                                        {appt.pName}
                                      </strong>
                                      <small className="text-muted">
                                        Age: {appt.age || "N/A"} |{" "}
                                        {appt.contact || "N/A"}
                                      </small>
                                    </div>
                                  </td>
                                  <td>
                                    <code className="bg-light px-2 py-1 rounded">
                                      {appt.mrCode || "N/A"}
                                    </code>
                                  </td>
                                  <td>
                                    {appt.vitals &&
                                    (appt.vitals.bp || appt.vitals.weight) ? (
                                      <small className="d-block">
                                        {appt.vitals.bp && (
                                          <div>
                                            <i className="bi bi-heart-pulse me-1 text-danger"></i>
                                            {appt.vitals.bp}
                                          </div>
                                        )}
                                        {appt.vitals.weight && (
                                          <div>
                                            <i className="bi bi-speedometer2 me-1 text-info"></i>
                                            {appt.vitals.weight}kg
                                          </div>
                                        )}
                                      </small>
                                    ) : (
                                      <small className="text-muted fst-italic">
                                        Not recorded
                                      </small>
                                    )}
                                  </td>
                                  {/* ✅ CHANGED: Convert time to 12-hour format */}
                                  <td>
                                    <small className="fw-semibold">
                                      {convertTo12Hour(appt.time)}
                                    </small>
                                  </td>
                                  {/* ✅ NEW: Display waiting time */}
                                  <td>
                                    <span 
                                      className={`badge bg-${getWaitingTimeBadge(appt.waitingTime)}`}
                                      title="Estimated waiting time set by receptionist"
                                    >
                                      <i className="bi bi-clock me-1"></i>
                                      {appt.waitingTime || 30} min
                                    </span>
                                  </td>
                                  <td>
                                    <small>{appt.department}</small>
                                  </td>
                                  <td>
                                    <div className="d-flex gap-2 justify-content-center flex-wrap">
                                      <button
                                        className={`btn btn-sm ${
                                          appt.status === "on-hold"
                                            ? "btn-warning"
                                            : "btn-success"
                                        }`}
                                        onClick={() => handleConsultClick(appt)}
                                      >
                                        <i
                                          className={`bi ${
                                            appt.status === "on-hold"
                                              ? "bi-arrow-clockwise"
                                              : "bi-clipboard2-pulse"
                                          } me-1`}
                                        ></i>
                                        {appt.status === "on-hold"
                                          ? "Continue"
                                          : "Consult"}
                                      </button>
                                      {appt.patientId && (
                                        <button
                                          className="btn btn-sm btn-outline-info"
                                          onClick={() =>
                                            handleViewHistory(
                                              appt.patientId._id ||
                                                appt.patientId
                                            )
                                          }
                                        >
                                          <i className="bi bi-clock-history me-1"></i>
                                          History
                                        </button>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </>
                  )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showConsultModal && selectedAppointment && (
        <ConsultPatientModal
          appointment={selectedAppointment}
          doctor={userInfo?.user?.name}
          onClose={() => {
            setShowConsultModal(false);
            setSelectedAppointment(null);
            dispatch(getDoctorAppointments());
            dispatch(getAllPendingAppointments());
            dispatch(getDoctorStats());
          }}
        />
      )}

      {showHistoryModal && historyPatientId && (
        <ViewPatientHistoryModal
          patientId={historyPatientId}
          onClose={() => {
            setShowHistoryModal(false);
            setHistoryPatientId(null);
          }}
        />
      )}

      {showCompletedHistory && (
        <CompletedAppointmentsHistory
          onClose={() => setShowCompletedHistory(false)}
        />
      )}
    </div>
  );
};

export default DoctorDashboard;